import * as pdfjsLib from 'pdfjs-dist';

// Set up the worker - use CDN for simplicity
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

/**
 * Converts the first page of a PDF file to a PNG image file
 * @param pdfFile - The PDF file to convert
 * @param scale - The scale factor for rendering (default 2 for good quality)
 * @returns A new File object containing the PNG image
 */
export async function convertPdfToImage(pdfFile: File, scale: number = 2): Promise<File> {
  // Read the PDF file as ArrayBuffer
  const arrayBuffer = await pdfFile.arrayBuffer();
  
  // Load the PDF document
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  
  // Get the first page
  const page = await pdf.getPage(1);
  
  // Get the viewport at the specified scale
  const viewport = page.getViewport({ scale });
  
  // Create a canvas to render the PDF page
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  
  if (!context) {
    throw new Error('Failed to get canvas context');
  }
  
  // Set canvas dimensions
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  
  // Render the page to the canvas
  await page.render({
    canvasContext: context,
    viewport: viewport,
  }).promise;
  
  // Convert canvas to blob
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to convert canvas to blob'));
        }
      },
      'image/png',
      1.0
    );
  });
  
  // Create a new File from the blob
  // Use the original filename but with .png extension
  const originalName = pdfFile.name.replace(/\.pdf$/i, '');
  const newFileName = `${originalName}.png`;
  
  return new File([blob], newFileName, { type: 'image/png' });
}

/**
 * Check if a file is a PDF
 */
export function isPdfFile(file: File): boolean {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}
